import { useEffect } from "react";
import { useAuth } from "../core/auth-context";
import { env } from "@monorepo/config";

/**
 * useGuestGuard Hook
 * Automatically redirects authenticated users away from guest-only pages.
 *
 * SECURITY AUDIT:
 * 1. Removed `import.meta.env.PROD` check. Logic must execute in DEV to catch regressions.
 * 2. Added support for `Maps` callback to allow SPA transitions instead of full reloads.
 *
 * @param redirectUrl - Target URL (default: Dashboard)
 * @param navigate - Optional callback for client-side navigation (e.g. from useNavigate)
 */
export function useGuestGuard(
  redirectUrl: string = env.signinRedirectUrl,
  navigate?: (path: string, options?: any) => void,
  disabled: boolean = false
) {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated && !disabled) {
      const isAbsolute =
        redirectUrl.startsWith("http://") || redirectUrl.startsWith("https://");

      if (navigate && !isAbsolute) {
        // PERF: Use client-side routing if provided to preserve state/speed
        navigate(redirectUrl, { replace: true });
      } else {
        // If the URL is the API redirect URL, we must fetch the target first to get JSON redirection
        if (redirectUrl.includes("/login/user/redirect")) {
          const fetchAndRedirect = async () => {
            try {
              const response = await fetch(redirectUrl, {
                method: "GET",
                credentials: "include",
                headers: { "Accept": "application/json" },
              });
              if (response.ok) {
                const data = await response.json();
                if (data.redirect_to) {
                  window.location.href = data.redirect_to;
                  return;
                }
              }
            } catch (err) {
              console.warn("Failed to fetch redirect JSON inside guest guard, falling back", err);
            }
            window.location.assign(redirectUrl);
          };
          fetchAndRedirect();
        } else {
          // FALLBACK: Hard redirect for cross-app boundaries (or external URLs)
          window.location.assign(redirectUrl);
        }
      }
    }
  }, [isAuthenticated, redirectUrl, navigate, disabled]);
}